/**
 * Consent Management System
 * ==========================
 * GDPR-compliant cookie consent with Google Consent Mode v2
 */

// Default consent state (denied until user accepts)
export const DEFAULT_CONSENT = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  functionality_storage: 'granted',
  personalization_storage: 'denied',
  security_storage: 'granted',
};

// Consent categories for user-facing UI
export const CONSENT_CATEGORIES = {
  necessary: {
    id: 'necessary',
    name: 'Necessary',
    description: 'Essential cookies required for the website to function properly. These cannot be disabled.',
    required: true,
    gtmConsent: ['functionality_storage', 'security_storage'],
  },
  analytics: {
    id: 'analytics',
    name: 'Analytics',
    description: 'Help us understand how visitors interact with our website by collecting anonymous information.',
    required: false,
    gtmConsent: ['analytics_storage'],
  },
  marketing: {
    id: 'marketing',
    name: 'Marketing',
    description: 'Used to track visitors across websites to display relevant advertisements.',
    required: false,
    gtmConsent: ['ad_storage', 'ad_user_data', 'ad_personalization'],
  },
  personalization: {
    id: 'personalization',
    name: 'Personalization',
    description: 'Allow the website to remember your preferences and provide enhanced features.',
    required: false,
    gtmConsent: ['personalization_storage'],
  },
};

// GA4 and GTM IDs from environment
const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || '';
const GTM_CONTAINER_ID = process.env.REACT_APP_GTM_CONTAINER_ID || '';

/**
 * Initialize Google Consent Mode v2 (must be called before GTM/GA4)
 */
export const initializeConsentMode = () => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  // Set default consent BEFORE any Google tags load
  gtag('consent', 'default', {
    ...DEFAULT_CONSENT,
    wait_for_update: 500, // Wait 500ms for consent update
  });

  // Set region-specific defaults (stricter for EU)
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    region: ['EU', 'EEA', 'GB', 'AU'], // Apply to these regions
  });
};

/**
 * Initialize Google Tag Manager
 */
export const initializeGTM = () => {
  if (!GTM_CONTAINER_ID || typeof window === 'undefined') return;

  // Ensure consent mode is initialized first
  initializeConsentMode();

  window.dataLayer = window.dataLayer || [];
  
  // GTM initialization
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });

  // Load GTM script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  document.head.appendChild(script);
};

/**
 * Initialize Google Analytics 4
 */
export const initializeGA4 = () => {
  if (!GA4_MEASUREMENT_ID || typeof window === 'undefined') return;

  // Ensure consent mode is initialized first
  if (!window.gtag) {
    initializeConsentMode();
  }

  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Configure GA4 with privacy-first settings
  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: false, // Manual page view tracking
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
};

/**
 * Update consent state (called when user makes choice)
 */
export const updateGoogleConsent = (consentState) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  const consentUpdate = { ...DEFAULT_CONSENT };
  
  // Map user consent to Google consent types
  Object.entries(consentState).forEach(([category, granted]) => {
    const categoryConfig = CONSENT_CATEGORIES[category];
    if (categoryConfig && granted) {
      categoryConfig.gtmConsent.forEach(consentType => {
        consentUpdate[consentType] = 'granted';
      });
    }
  });

  // Update Google consent
  window.gtag('consent', 'update', consentUpdate);

  // Push consent event to dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'consent_update',
    consent_analytics: consentState.analytics ? 'granted' : 'denied',
    consent_marketing: consentState.marketing ? 'granted' : 'denied',
    consent_personalization: consentState.personalization ? 'granted' : 'denied',
  });

  // If analytics granted, send page view
  if (consentState.analytics && GA4_MEASUREMENT_ID) {
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
    });
  }
};

/**
 * Store consent in localStorage with metadata
 */
export const storeConsent = (consentState) => {
  if (typeof window === 'undefined') return;
  
  const consentData = {
    ...consentState,
    timestamp: new Date().toISOString(),
    version: '2.0',
    userAgent: navigator.userAgent,
  };
  
  localStorage.setItem('corvana_consent', JSON.stringify(consentData));
  
  // Also update Google consent
  updateGoogleConsent(consentState);

  // Notify first-party trackers (SiteAnalytics) that consent changed.
  try { window.dispatchEvent(new Event('cv-consent-changed')); } catch { /* ignore */ }
};

/**
 * Get stored consent from localStorage
 */
export const getStoredConsent = () => {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem('corvana_consent');
    if (!stored) return null;
    
    const consentData = JSON.parse(stored);
    
    // Check expiry (12 months per GDPR guidelines)
    const consentDate = new Date(consentData.timestamp);
    const expiryDate = new Date(consentDate);
    expiryDate.setMonth(expiryDate.getMonth() + 12);
    
    if (new Date() > expiryDate) {
      localStorage.removeItem('corvana_consent');
      return null;
    }
    
    return consentData;
  } catch {
    return null;
  }
};

/**
 * Check if user has made consent choice
 */
export const hasConsentChoice = () => {
  return getStoredConsent() !== null;
};

/**
 * Accept all cookies
 */
export const acceptAllCookies = () => {
  const consentState = {
    necessary: true,
    analytics: true,
    marketing: true,
    personalization: true,
  };
  
  storeConsent(consentState);
  return consentState;
};

/**
 * Accept only necessary cookies
 */
export const acceptNecessaryOnly = () => {
  const consentState = {
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false,
  };
  
  storeConsent(consentState);
  return consentState;
};

/**
 * Save custom consent preferences
 */
export const saveConsentPreferences = (preferences) => {
  const consentState = {
    necessary: true,
    analytics: preferences.analytics || false,
    marketing: preferences.marketing || false,
    personalization: preferences.personalization || false,
  };
  
  storeConsent(consentState);
  return consentState;
};

/**
 * Revoke consent (clear all)
 */
export const revokeConsent = () => {
  if (typeof window === 'undefined') return;
  
  localStorage.removeItem('corvana_consent');
  
  // Reset to default denied state
  if (window.gtag) {
    window.gtag('consent', 'update', DEFAULT_CONSENT);
  }

  try { window.dispatchEvent(new Event('cv-consent-changed')); } catch { /* ignore */ }
};

/**
 * Initialize consent system on app load
 */
export const initializeConsent = () => {
  // Initialize consent mode first (before any tags)
  initializeConsentMode();
  
  // Initialize GTM/GA4
  initializeGTM();
  initializeGA4();
  
  // Check for stored consent and apply
  const storedConsent = getStoredConsent();
  if (storedConsent) {
    updateGoogleConsent(storedConsent);
  }
};

export default {
  initializeConsent,
  initializeConsentMode,
  initializeGTM,
  initializeGA4,
  updateGoogleConsent,
  storeConsent,
  getStoredConsent,
  hasConsentChoice,
  acceptAllCookies,
  acceptNecessaryOnly,
  saveConsentPreferences,
  revokeConsent,
  CONSENT_CATEGORIES,
  DEFAULT_CONSENT,
};
